import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { toast } from "sonner";
import { UserCog, UserPlus, KeyRound, Power, PowerOff, Pencil, ScrollText, Search, X } from "lucide-react";

const ROLES = ["owner", "manager", "technician"];

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} data-testid={testid}>
      <div className="sg-card p-6 w-full max-w-md sg-fade">
        <div className="flex items-center justify-between mb-4">
          <div className="font-head text-xl font-bold">{title}</div>
          <button onClick={onClose} className="sg-btn" data-testid="modal-close" style={{ padding: "6px" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [tab, setTab] = useState("employees");
  const [users, setUsers] = useState(null);
  const [crews, setCrews] = useState([]);
  const [logs, setLogs] = useState(null);
  const [logFilter, setLogFilter] = useState("");
  const [editing, setEditing] = useState(null);   // user object or "new"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    loadUsers();
    api.get("/crews").then(({ data }) => setCrews(data)).catch(() => {});
  }, [loadUsers]);

  useEffect(() => {
    if (tab === "audit" && logs === null) api.get("/audit-logs?limit=300").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, [tab, logs]);

  const openNew = () => { setForm({ name: "", email: "", role: "technician", crew_id: "", hourly_wage: 15, invite: true, password: "" }); setEditing("new"); };
  const openEdit = (u) => { setForm({ name: u.name, role: u.role, crew_id: u.crew_id || "", hourly_wage: u.hourly_wage ?? 15 }); setEditing(u); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        const payload = { name: form.name, email: form.email, role: form.role, crew_id: form.crew_id || null, hourly_wage: Number(form.hourly_wage) };
        if (!form.invite) payload.password = form.password;
        const { data } = await api.post("/users", payload);
        toast.success(data.invited ? "Invite sent — employee will set their password" : "Employee created");
      } else {
        await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, crew_id: form.crew_id || null, hourly_wage: Number(form.hourly_wage) });
        toast.success("Employee updated");
      }
      setEditing(null);
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const act = async (u, kind) => {
    try {
      if (kind === "reset") { const { data } = await api.post(`/users/${u.id}/reset-access`); toast.success(data.message || "Reset link sent"); }
      else { await api.post(`/users/${u.id}/${kind}`); toast.success(kind === "activate" ? "Access restored" : "Access disabled"); await loadUsers(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (users === null) return <Loading label="Loading team" />;

  const isActive = (u) => u.active !== false;
  const filteredLogs = (logs || []).filter((l) =>
    !logFilter || `${l.action} ${l.entity} ${l.user_name} ${l.user_role} ${JSON.stringify(l.details || {})}`.toLowerCase().includes(logFilter.toLowerCase()));

  return (
    <div className="max-w-5xl" data-testid="team-page">
      <SectionTitle sub="Owner-only · manage employee access, roles and the privileged audit trail">Team &amp; Access</SectionTitle>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("employees")} data-testid="tab-employees"
          className={`sg-btn ${tab === "employees" ? "sg-btn-accent" : ""}`}><UserCog size={16} /> Employees</button>
        <button onClick={() => setTab("audit")} data-testid="tab-audit"
          className={`sg-btn ${tab === "audit" ? "sg-btn-accent" : ""}`}><ScrollText size={16} /> Audit Log</button>
      </div>

      {tab === "employees" && (
        <>
          <div className="flex justify-end mb-4">
            <button className="sg-btn sg-btn-accent" onClick={openNew} data-testid="add-employee-btn"><UserPlus size={16} /> Add employee</button>
          </div>
          <div className="flex flex-col gap-3">
            {users.map((u) => (
              <div key={u.id} className="sg-card p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`user-row-${u.id}`}>
                <div className="min-w-[220px]">
                  <div className="font-semibold flex items-center gap-2">
                    {u.name}
                    {!isActive(u) && <span className="chip" style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", borderColor: "#EF4444", fontSize: 10 }} data-testid={`status-inactive-${u.id}`}>Deactivated</span>}
                  </div>
                  <div className="text-xs break-all" style={{ color: "var(--txt2)" }}>{u.email}</div>
                </div>
                <StatusChip status="certified" label={u.role} />
                <div className="flex flex-wrap gap-2">
                  <button className="sg-btn" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil size={14} /> Edit</button>
                  <button className="sg-btn" onClick={() => act(u, "reset")} data-testid={`reset-user-${u.id}`}><KeyRound size={14} /> Reset access</button>
                  {isActive(u)
                    ? <button className="sg-btn" onClick={() => act(u, "deactivate")} data-testid={`deactivate-user-${u.id}`} disabled={u.id === user.id}><PowerOff size={14} /> Deactivate</button>
                    : <button className="sg-btn sg-btn-accent" onClick={() => act(u, "activate")} data-testid={`activate-user-${u.id}`}><Power size={14} /> Activate</button>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "audit" && (
        <div className="sg-card p-4" data-testid="audit-log">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--txt2)" }} />
            <input className="sg-input pl-9" placeholder="Filter by action, user, entity…" value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)} data-testid="audit-filter" />
          </div>
          {logs === null ? <Loading label="Loading audit log" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr style={{ color: "var(--txt2)" }} className="text-left text-xs uppercase tracking-wider">
                  <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Actor</th><th className="py-2 pr-3">Action</th><th className="py-2 pr-3">Entity</th><th className="py-2">Details</th>
                </tr></thead>
                <tbody>
                  {filteredLogs.map((l, i) => (
                    <tr key={l.id || i} className="border-t" style={{ borderColor: "var(--brd)" }} data-testid={`audit-row-${i}`}>
                      <td className="py-2 pr-3 whitespace-nowrap" style={{ color: "var(--txt2)" }}>{new Date(l.created_at).toLocaleString()}</td>
                      <td className="py-2 pr-3">{l.user_name} <span style={{ color: "var(--txt2)" }}>· {l.user_role}</span></td>
                      <td className="py-2 pr-3 font-mono text-xs">{l.action}</td>
                      <td className="py-2 pr-3">{l.entity}</td>
                      <td className="py-2 font-mono text-xs" style={{ color: "var(--txt2)" }}>{Object.keys(l.details || {}).length ? JSON.stringify(l.details) : "—"}</td>
                    </tr>
                  ))}
                  {filteredLogs.length === 0 && <tr><td colSpan={5} className="py-6 text-center" style={{ color: "var(--txt2)" }}>No matching entries</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editing && (
        <Modal title={editing === "new" ? "Add employee" : `Edit ${editing.name}`} onClose={() => setEditing(null)} testid="employee-modal">
          <div className="flex flex-col gap-3">
            {editing === "new" && (
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Email</label>
                <input className="sg-input mt-1" type="email" value={form.email} data-testid="form-email"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Name</label>
              <input className="sg-input mt-1" value={form.name} data-testid="form-name"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Role</label>
                <select className="sg-input mt-1" value={form.role} data-testid="form-role"
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Hourly wage</label>
                <input className="sg-input mt-1" type="number" value={form.hourly_wage} data-testid="form-wage"
                  onChange={(e) => setForm((f) => ({ ...f, hourly_wage: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Crew</label>
              <select className="sg-input mt-1" value={form.crew_id} data-testid="form-crew"
                onChange={(e) => setForm((f) => ({ ...f, crew_id: e.target.value }))}>
                <option value="">— None —</option>
                {crews.map((c) => <option key={c.id} value={c.id}>{c.crew_number}</option>)}
              </select>
            </div>
            {editing === "new" && (
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="form-invite-toggle">
                <input type="checkbox" checked={form.invite} onChange={(e) => setForm((f) => ({ ...f, invite: e.target.checked }))} />
                Email a secure "set your password" invite (recommended)
              </label>
            )}
            {editing === "new" && !form.invite && (
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Temporary password (min 8)</label>
                <input className="sg-input mt-1" type="text" value={form.password} data-testid="form-password"
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              </div>
            )}
            <button className="sg-btn sg-btn-accent mt-2" onClick={save} disabled={busy} data-testid="save-employee-btn">
              {busy ? "Saving…" : editing === "new" ? "Create employee" : "Save changes"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
